import './App.css';
// FE-TX-UI-RESTORE: shared design system for the 16 Texas route pages. The
// components emit a semantic class vocabulary (texas-page-container, search-bar,
// action-bar, timeline-table, ...) that had no stylesheet - this defines it once.
// claude-code/opencode lanes: do NOT duplicate this import or redefine these
// classes; extend texas-pages.css instead.
import './components/Texas/texas-pages.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { getCurrentUser } from './services/cognito';
import { buildUserFromToken } from './utils/buildUserFromToken';
import HomePage from './components/HomePage';
import LandingPage from './components/LandingPage';
import SignUpForm from './components/SignUpForm';
import LoginForm from './components/LoginForm';
import AuthCallback from './components/AuthCallback';
import RenewalPage from './components/RenewalPage';
import ProductSelector from './components/ProductSelector';
// T-242: /pricing is live for the us2 lane (top-up/upgrade target for the
// T-146-002 allowance widget CTAs). Storefront resolves via fastspringStorefront.
import PricingPage from './components/PricingPage';
// F-201 / T-151-001: /compare — plan comparison + recommendation quiz.
import PlanComparePage from './components/Compare/PlanComparePage';
// F-201 / T-152-002: contextual trial conversion prompts (AC-5).
import TrialConversionModal from './components/Trial/TrialConversionModal';
import Confirm from './components/confirm';
import AdminUsers from './components/AdminUsers';
import FreeTrial from './components/FreeTrial';
import Dashboard from './components/Dashboard'; // Placeholder
import ArrearsDashboard from './components/Arrears';
import Communications from './components/Communications';
import EvictionWorkflow from './components/EvictionWorkflow';
import ForgotPassword from './components/ForgotPassword';
import Properties from './components/Properties';
import AddProperty from './components/Properties/AddProperty';
import PropertyDetail from './components/Properties/PropertyDetail';
import ComplianceCalendar from './components/Calendar';
import Settings from './components/Settings';
import Features from './components/Features/Features';
import Support from './components/Support';
import GDPRDashboard from './components/GDPR';
import Certificates from './components/Certificates';
import Tenants from './components/Tenants';
import RepairsTrackingPage from './components/Repairs';
import AwaabLawPage from './components/Awaab/AwaabLawPage';
import SiloBreakerPage from './components/SiloBreaker';
import TeamManagement from './components/Team';
import SuperAdmin from './components/SuperAdmin';

import TenantPortal from './components/Portal/TenantPortal';
import TenantPaymentPortal from './components/TenantPayments/TenantPaymentPortal';
import HazardReport from './components/Portal/HazardReport';
import VerifyReceipt from './components/VerifyReceipt';
import FeedbackWidget from './components/FeedbackWidget';
import RefreshWarning from './components/RefreshWarning';
import NLPAnalyzer from './components/NLPAnalyzer';
import PageViewTracker from './components/PageViewTracker';
import SystemBanner from './components/Shared/SystemBanner';
import ConsentBanner from './components/Shared/ConsentBanner';
import { featureFlags, setTenantFeatures } from './utils/featureFlags';
import { featureService } from './services/api';
import { initGA, logPageView, initAnalytics } from './services/analytics';
import { useLocation } from 'react-router-dom';
// Food Safety Pro Components
import { FoodSafetyDashboard, FoodSafetyLandingPage } from './components/FoodSafety';
import FinanceDashboard from './components/Finance/FinanceDashboard';
import HousingDashboard from './components/HousingPro/HousingDashboard';
import TenancyAgreementManager from './components/Agreements/TenancyAgreementManager';
import AMLDashboard from './components/AML';
import SecurityPage from './components/SecurityPage';
import LegalQA from './components/LegalQA';
import InvitePartner from './components/InvitePartner';
import PartnersLanding from './components/Partners';
import LocalGovLanding from './components/LocalGov';
import AffiliateDashboard from './components/Affiliates/AffiliateDashboard';
import CertificateOrdering from './components/CertificateOrdering';
import AffiliatesAdmin from './components/AdminDashboards/AffiliatesAdmin';
import PartnersAdmin from './components/AdminDashboards/PartnersAdmin';
import SSOLogin from './components/Auth/SSOLogin';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import { JurisdictionProvider } from './context/JurisdictionContext';
import HubLanding from './components/Hub/HubLanding';
import MarketService, { getAppMarket } from './services/MarketService';
import { isUkOnlyPath } from './nav/texasMarketGate';
import TexasDashboard from './components/Texas/TexasDashboard';
import TexasLandingPage from './components/Texas/TexasLandingPage';
import { TexasGroupedSidebar } from './nav/TexasGroupedSidebar';
import { getTexasRoutePaths, getTexasNavItems } from './nav/routeRegistry';
import { SubchapterDChecklist } from './components/Texas/SubchapterDChecklist';
import { RekeyCountdown } from './components/Texas/RekeyCountdown';
import { LeadPaintDisclosure } from './components/Texas/LeadPaintDisclosure';
import { SB38NoticeGenerator } from './components/Texas/SB38NoticeGenerator';
import { SB38Timeline } from './components/Texas/SB38Timeline';
import { MunicipalRegistrationsHub } from './components/Texas/MunicipalRegistrationsHub';
import { ScannerDashboard } from './compliance/scanner/ScannerDashboard';
import { DsrPortal } from './compliance/dsr/DsrPortal';
import { SecurityAuditDashboard } from './security/audit/SecurityAuditDashboard';
import { CcpaPlaybook } from './security/playbooks/CcpaPlaybook';
import { TxPlaybook } from './security/playbooks/TxPlaybook';
import { HVACReadinessPage } from './components/Texas/HVACReadinessPage';
import './App.css';
import { EvidenceHub } from './components/Texas/EvidenceHub';
import { DepositTracker } from './components/Texas/DepositTracker';
import { NoticeVacate } from './components/Texas/NoticeVacate';
import { TexasAnalytics } from './components/Texas/TexasAnalytics';
import { TexasScore } from './components/Texas/TexasScore';
import { FinanceExports } from './components/Texas/FinanceExports';
import { StrPortfolioDashboard } from './components/Texas/StrPortfolioDashboard';
import { TexasComplianceConsole } from './components/Texas/TexasComplianceConsole';
import { TexasEventStreamOps } from './components/Texas/TexasEventStreamOps';
import { TexasTemplateLibrary } from './components/Texas/TexasTemplateLibrary';
import { TexasDeadlineMonitor } from './components/Texas/TexasDeadlineMonitor';
import { SecurityScorePage } from './components/Texas/SecurityScorePage';
import { AsecRemediationPage } from './components/Texas/AsecRemediationPage';
import { UsdPricingAdmin } from './components/Texas/UsdPricingAdmin';
import { NeedsAssessmentWizard } from './components/Texas/needs-assessment/NeedsAssessmentWizard';
import { SmartDocumentReader } from './components/Texas/SmartDocumentReader';
import { TierWelcome } from './components/Texas/TierWelcome';
import { TexasRouteShell } from './nav/TexasRouteShell';
import { ConversionFunnelDashboard } from './components/Dashboards/ConversionFunnelDashboard';
import { MrrDashboard } from './components/Dashboards/MrrDashboard';
import LeadScoringDashboard from './components/Dashboards/LeadScoringDashboard';
import LinkedInOrganicDashboard from './components/Dashboards/LinkedInOrganicDashboard';
import MultiCityComplianceDashboard from './components/Texas/MultiCityComplianceDashboard';
import HRRBRegistrationsPage from './components/Texas/HRRBRegistrationsPage';
import FloodComplianceDashboard from './components/Texas/FloodComplianceDashboard';
import CityOverridesAdmin from './components/Texas/CityOverridesAdmin';
import { TexasOpsConsole } from './components/Texas/TexasOpsConsole';
import { LinkedInTemplateLibrary } from './components/Growth/LinkedInTemplateLibrary';
import { EnterpriseSsoLeadForm } from './components/Growth/EnterpriseSsoLeadForm';
import { TexasLeadQualification } from './components/Growth/TexasLeadQualification';
import { TexasSequenceBuilder } from './components/Growth/TexasSequenceBuilder';
import { TexasSocialScheduler } from './components/Growth/TexasSocialScheduler';
import { TexasContentStudio } from './components/Growth/TexasContentStudio';
import ROICalculatorPage from './components/ROICalculator/ROICalculatorPage';
import { EvidenceSafeRouteGate, EVIDENCE_SAFE_ROUTES } from './gates/evidence-safe';
import { SmartReaderRouteGate, SMART_READER_ROUTES } from './gates/smart-reader';

const TexasPagePlaceholder: React.FC<{ title: string }> = ({ title }) => (
    <div data-testid="texas-page-placeholder" className="hp-dashboard-container">
        <h1>{title}</h1>
        <p>Page implementation pending — F-291</p>
    </div>
);

/** F-203: legacy /payment-success → /texas/welcome (keeps plan context). */
const LegacyPaymentSuccessRedirect: React.FC = () => {
    const [searchParams] = useSearchParams();
    const tier = searchParams.get('product') || searchParams.get('tier');
    return (
        <Navigate
            to={tier ? `/texas/welcome?tier=${encodeURIComponent(tier)}` : '/texas/welcome'}
            replace
        />
    );
};

const TexasRouteComponents: Record<string, React.ComponentType<any>> = {
    '/texas/subchapter-d': SubchapterDChecklist,
    '/texas/rekey': RekeyCountdown,
    '/texas/lead-paint': LeadPaintDisclosure,
    '/texas/compliance-scan': ScannerDashboard,
    '/texas/compliance-dsr': DsrPortal,
    '/texas/security-audit': SecurityAuditDashboard,
    '/texas/playbook-ccpa': CcpaPlaybook,
    '/texas/playbook-tx': TxPlaybook,
    '/texas/registrations': MunicipalRegistrationsHub,
    '/texas/hvac': HVACReadinessPage,
    '/texas/sb38-notice': SB38NoticeGenerator,
    '/texas/sb38-timeline': SB38Timeline,
    '/texas/deposits': DepositTracker,
    '/texas/notice-vacate': NoticeVacate,
    '/texas/evidence': EvidenceHub,
    '/texas/analytics': TexasAnalytics,
    '/texas/score': TexasScore,
    '/texas/finance': FinanceExports,
    '/texas/str-portfolio': StrPortfolioDashboard,
    '/texas/compliance-console': TexasComplianceConsole,
'/texas/event-stream-ops': TexasEventStreamOps,
    '/texas/template-library': TexasTemplateLibrary,
    '/texas/deadline-monitor': TexasDeadlineMonitor,
    '/texas/security-score': SecurityScorePage,
    '/texas/smart-reader': SmartDocumentReader,
    '/texas/asec': AsecRemediationPage,
    '/texas/pricing-admin': UsdPricingAdmin,
    '/texas/assessment': NeedsAssessmentWizard,
    '/texas/compliance-portfolio': MultiCityComplianceDashboard,
    '/texas/hrrb': HRRBRegistrationsPage,
    '/texas/flood': FloodComplianceDashboard,
    '/texas/city-overrides': CityOverridesAdmin,
    '/texas/ops-console': TexasOpsConsole,
    '/texas/linkedin-library': LinkedInTemplateLibrary,
    '/texas/enterprise-sso': EnterpriseSsoLeadForm,
    '/texas/lead-qualification': TexasLeadQualification,
    '/texas/email-sequence': TexasSequenceBuilder,
    '/texas/social-scheduler': TexasSocialScheduler,
    '/texas/content-studio': TexasContentStudio,
};

// F-308: UK / PRS features are unreachable in the Texas market. Wrapping an
// element in this gate is a no-op for every other market — the component tree
// is never deleted, only unlinked for texas. Paths live in nav/texasMarketGate.
const UkOnlyGate: React.FC<{ path: string; children: React.ReactNode }> = ({ path, children }) => {
    if (getAppMarket() !== 'texas') return <>{children}</>;
    if (isUkOnlyPath(path)) return <Navigate to="/dashboard" replace />;
    return <>{children}</>;
};

// Helper component to handle domain-based routing with logging
const RootRoute = () => {
  const hostname = window.location.hostname.toLowerCase();
  // Check for FSP domains
  // Matches: fsp.compliance-engine.io, fsp-dev..., foodsafety-pro...
  const isFSP = hostname.includes('fsp') || hostname.includes('food-safety') || hostname.includes('foodsafety');

  // Market Hub routing (F-272): the hub build (REACT_APP_MARKET=hub) serves
  // the global HubLanding with the country selector. UK/Texas market apps
  // (uk./texas. subdomains) skip the hub and render their regional landing.
  const appMarket = getAppMarket();
  const isMarketDomain = MarketService.shouldSkipHub(hostname);
  const isHub =
    appMarket === 'hub' ||
    (!isMarketDomain && hostname.includes('orange-mud')) ||
    (!isMarketDomain && (hostname === 'landlord.compliance-engine.io'));

  console.log(`[RootRoute v1.2] Hostname: ${hostname}, isFSP: ${isFSP}, market: ${appMarket}, isHub: ${isHub}`);

  if (isHub) {
    return <HubLanding />;
  }

  if (isFSP) {
    return <FoodSafetyLandingPage />;
  }

  // Market-specific landing pages (F-277 Texas Landing Page)
  if (appMarket === 'texas') {
    return <TexasLandingPage />;
  }

  return <LandingPage />;
};

// Cache the Housing Pro answer in localStorage so we route correctly on
// the FIRST render (no race against the features fetch). The cache is
// refreshed every time features load — so toggling propertyGroups via
// Super Admin takes effect on the next session.
const HOUSING_PRO_CACHE_KEY = 'lp_housing_pro_mode';
const readHousingProCache = (): boolean => {
  try { return localStorage.getItem(HOUSING_PRO_CACHE_KEY) === 'true'; }
  catch { return false; }
};
const writeHousingProCache = (on: boolean) => {
  try { localStorage.setItem(HOUSING_PRO_CACHE_KEY, on ? 'true' : 'false'); }
  catch { /* ignore */ }
};

/** Loading screen shown on /dashboard while we wait for the features fetch
 *  to resolve. Prevents the "lands on Landlord Pro then flips to Housing Pro
 *  after refresh" race seen on first login. */
function DashboardLoadingScreen() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mb-4"></div>
        <p className="text-sm text-slate-600 font-medium">Loading your dashboard…</p>
      </div>
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  // Seed from cache so returning users route to the right dashboard immediately
  const [housingProReady, setHousingProReady] = useState(readHousingProCache());
  const [housingProMode,  setHousingProMode]  = useState(readHousingProCache());

  // Initialize Google Analytics + Mixpanel facade (consent-gated)
  useEffect(() => {
    initGA();
    initAnalytics();
  }, []);

  // Check authentication
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const currentUser = getCurrentUser();
        if (currentUser) {
          const idToken = localStorage.getItem('idToken');
          if (idToken) {
            try {
              // Fetch features in background + cache the Housing Pro answer
              // so the NEXT page load routes correctly without a race.
              featureService.getFeatures().then((res: any) => {
                  if (res?.data?.success) setTenantFeatures(res.data.data);
                  const isHP = !!(res?.data?.data?.propertyGroups?.enabled);
                  writeHousingProCache(isHP);
                  setHousingProMode(isHP);
                  setHousingProReady(true);
              }).catch((e: any) => {
                  console.error('[App] Feature fetch failed', e);
                  setHousingProReady(true);
              });

              setUser(buildUserFromToken(currentUser, idToken));
              setIsAuthenticated(true);
            } catch (e) {
              console.error('Error decoding token', e);
              setUser(currentUser);
              setIsAuthenticated(true);
            }
          } else {
            setUser(currentUser);
            setIsAuthenticated(true);
          }
        }
      } catch (globalAuthErr) {
        console.error('[App] Critical Auth Check Error:', globalAuthErr);
      } finally {
        setIsAuthChecking(false);
      }
    };

    // Watchdog: If auth takes too long, stop checking to avoid white-screen lockout
    const timer = setTimeout(() => {
        if (isAuthChecking) {
            console.warn('[App] Watchdog: Auth check timed out. Proceeding to mount.');
            setIsAuthChecking(false);
        }
    }, 10000);

    checkAuth();
    return () => clearTimeout(timer);
  }, []);

  // Re-run the Housing Pro features fetch whenever auth flips to true.
  // Without this the very first login (where checkAuth had already run on
  // App mount BEFORE tokens existed) leaves housingProReady=false forever,
  // forcing users to hard-refresh before the dashboard renders.
  // Subsequent logins on the same tab still work because the cache is
  // seeded — but a fresh account or post-signOut returning user hits this
  // path and sees the spinner spin forever.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (housingProReady) return;
    featureService.getFeatures()
      .then((res: any) => {
        if (res?.data?.success) setTenantFeatures(res.data.data);
        const isHP = !!(res?.data?.data?.propertyGroups?.enabled);
        writeHousingProCache(isHP);
        setHousingProMode(isHP);
        setHousingProReady(true);
      })
      .catch((e: any) => {
        console.error('[App] Feature fetch (post-auth) failed', e);
        // Fail open — let the dashboard render rather than spinning forever.
        setHousingProReady(true);
      });
  }, [isAuthenticated, housingProReady]);

  if (isAuthChecking) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  return (
    <Router>
      <JurisdictionProvider>
        <PageViewTracker>
          {isAuthenticated && <SystemBanner />}
          {isAuthenticated && <EmailVerificationBanner />}
          <div className="App">
          <Routes>
            <Route path="/portal/:token" element={<TenantPortal />} />
            <Route path="/tenant-payments/:token" element={<TenantPaymentPortal />} />
            <Route path="/portal/hazard-report/:token" element={<HazardReport />} />
            {/* Public receipt verification — the QR target on every printed receipt */}
            <Route path="/verify/receipt/:receiptId" element={<VerifyReceipt />} />
            <Route path="/signup" element={<SignUpForm />} />
            <Route path="/login" element={<LoginForm />} />
            <Route path="/login/sso/:orgSlug" element={<SSOLogin />} />
            <Route path="/auth/sso/:domain" element={<SSOLogin />} />
            <Route path="/auth/callback" element={<AuthCallback setAuthenticated={setIsAuthenticated} setUser={setUser} />} />
            <Route path="/renewal" element={<RenewalPage />} />
            <Route path="/product-selector" element={<ProductSelector />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/compare" element={<PlanComparePage />} />
            <Route path="/roi-calculator" element={<ROICalculatorPage />} />
            <Route path="/confirm" element={<Confirm />} />
            {/* <Route path="/payment-success" element={<PaymentSuccess />} /> */}
            {/* T-173/F-203: legacy TexasPricingSection lands on /payment-success
                after checkout — redirect to the tier welcome screen (confetti +
                updated badge). */}
            <Route path="/payment-success" element={<LegacyPaymentSuccessRedirect />} />
            <Route path="/texas/welcome" element={<TierWelcome />} />
            <Route path="/admin/users" element={isAuthenticated ? <AdminUsers /> : <Navigate to="/login" />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />



            <Route path="/freetrial/*" element={isAuthenticated ? <FreeTrial /> : <Navigate to="/login" />} />
            {/* Dashboard routing.
                Frame 1 — housingProReady is seeded from localStorage cache
                (true if the user has logged into this tenant before).
                  - Returning users: route immediately to the cached dashboard.
                  - First-time users: housingProReady=false → show loader until
                    the features fetch resolves (no flash of wrong dashboard).
                When features resolve we cache the answer for next time. */}
            <Route path="/dashboard" element={
                !isAuthenticated ? <Navigate to="/login" />
                : !housingProReady ? <DashboardLoadingScreen />
                : getAppMarket() === 'texas'
                    ? <TexasDashboard user={user} />
                : housingProMode
                    ? <HousingDashboard user={user} />
                    : <Dashboard user={user} />
            } />

            {/* F-289/F-290/F-291: Texas route registry pages.
                TexasRouteShell keeps the top menu bar on every child page —
                without it each page was a dead end with no nav (2026-10-06). */}
            {getTexasRoutePaths().map((path) => {
                const Component = TexasRouteComponents[path] || TexasPagePlaceholder;
                // US-162 / T-162-001: Evidence Safe + Delivery Proof are
                // Enterprise-only — gate before rendering the feature.
                const gated = Boolean(EVIDENCE_SAFE_ROUTES[path]);
                // US-163 / T-163-001: Smart Document Reader is Enterprise-only.
                const gatedSmartReader = Boolean(SMART_READER_ROUTES[path]);
                return (
                    <Route key={path} path={path} element={
                        isAuthenticated ? (
                            <TexasRouteShell>
                                {gated ? (
                                    <EvidenceSafeRouteGate><Component /></EvidenceSafeRouteGate>
                                ) : gatedSmartReader ? (
                                    <SmartReaderRouteGate><Component /></SmartReaderRouteGate>
                                ) : (
                                    <Component />
                                )}
                            </TexasRouteShell>
                        ) : (
                            <Navigate to="/login" />
                        )
                    } />
                );
            })}

            {/* BUG-5: /texas/deposit-tracker is the canonical URL E2E/links use —
                keep the registry path (/texas/deposits) for the sidebar and add
                this alias so the page never renders blank. */}
            <Route path="/texas/deposit-tracker" element={
                isAuthenticated ? (
                    <TexasRouteShell><DepositTracker /></TexasRouteShell>
                ) : (
                    <Navigate to="/login" />
                )
            } />

            {/* MTD (HMRC Making Tax Digital) is UK-only — descoped from the Texas
                market 2026-10-12. Keep a redirect so old links/bookmarks don't
                dead-end or render blank. */}
            <Route path="/texas/mtd" element={<Navigate to="/dashboard" replace />} />

            <Route path="/properties" element={isAuthenticated ? <Properties /> : <Navigate to="/login" />} />
            <Route path="/properties/add" element={isAuthenticated ? <AddProperty /> : <Navigate to="/login" />} />
            <Route path="/add-property" element={isAuthenticated ? <AddProperty /> : <Navigate to="/login" />} />


            <Route path="/properties/:id" element={isAuthenticated ? <PropertyDetail /> : <Navigate to="/login" />} />
            <Route path="/calendar" element={isAuthenticated ? <ComplianceCalendar /> : <Navigate to="/login" />} />
            <Route path="/certificates" element={<UkOnlyGate path="/certificates">{isAuthenticated ? <Certificates /> : <Navigate to="/login" />}</UkOnlyGate>} />
            <Route path="/tenants" element={<UkOnlyGate path="/tenants">{isAuthenticated ? <Tenants /> : <Navigate to="/login" />}</UkOnlyGate>} />
            <Route path="/repairs-tracking" element={<UkOnlyGate path="/repairs-tracking">{isAuthenticated ? <RepairsTrackingPage /> : <Navigate to="/login" />}</UkOnlyGate>} />
            <Route path="/awaab-law" element={<UkOnlyGate path="/awaab-law">{isAuthenticated ? <AwaabLawPage /> : <Navigate to="/login" />}</UkOnlyGate>} />
            <Route path="/case-linking" element={<UkOnlyGate path="/case-linking">{isAuthenticated ? <SiloBreakerPage /> : <Navigate to="/login" />}</UkOnlyGate>} />
            {featureFlags.EVICTIONS_ENABLED && <Route path="/evictions" element={<UkOnlyGate path="/evictions">{isAuthenticated ? <EvictionWorkflow /> : <Navigate to="/login" />}</UkOnlyGate>} />}
            <Route path="/communications" element={isAuthenticated ? <Communications /> : <Navigate to="/login" />} />
            {featureFlags.ARREARS_ENABLED && <Route path="/arrears" element={<UkOnlyGate path="/arrears">{isAuthenticated ? <ArrearsDashboard /> : <Navigate to="/login" />}</UkOnlyGate>} />}
            {featureFlags.ARREARS_ENABLED && <Route path="/arrears/summary" element={<UkOnlyGate path="/arrears/summary">{isAuthenticated ? <ArrearsDashboard /> : <Navigate to="/login" />}</UkOnlyGate>} />}
            <Route path="/agreements" element={<UkOnlyGate path="/agreements">{isAuthenticated ? <TenancyAgreementManager /> : <Navigate to="/login" />}</UkOnlyGate>} />
            <Route path="/aml" element={<UkOnlyGate path="/aml">{isAuthenticated ? <AMLDashboard /> : <Navigate to="/login" />}</UkOnlyGate>} />
            <Route path="/team" element={isAuthenticated ? <TeamManagement /> : <Navigate to="/login" />} />
            <Route path="/invite-partner" element={isAuthenticated ? <InvitePartner /> : <Navigate to="/login" />} />
            <Route path="/partners" element={<UkOnlyGate path="/partners"><PartnersLanding /></UkOnlyGate>} />
            <Route path="/local-gov/:orgId?" element={<UkOnlyGate path="/local-gov/:orgId?"><LocalGovLanding /></UkOnlyGate>} />
            <Route path="/super-admin" element={isAuthenticated && user?.isSuperAdmin ? <SuperAdmin /> : <Navigate to="/dashboard" />} />

            {/* CC-003 / US-155 + US-156 growth dashboards (admin-only endpoints) */}
            <Route path="/growth/funnel" element={isAuthenticated && (user?.isAdmin || user?.isSuperAdmin) ? <ConversionFunnelDashboard /> : <Navigate to="/dashboard" />} />
            <Route path="/growth/mrr" element={isAuthenticated && (user?.isAdmin || user?.isSuperAdmin) ? <MrrDashboard /> : <Navigate to="/dashboard" />} />
            <Route path="/growth/leads" element={isAuthenticated && (user?.isAdmin || user?.isSuperAdmin) ? <LeadScoringDashboard /> : <Navigate to="/dashboard" />} />
            <Route path="/growth/linkedin" element={isAuthenticated && (user?.isAdmin || user?.isSuperAdmin) ? <LinkedInOrganicDashboard /> : <Navigate to="/dashboard" />} />
            <Route path="/admin-portal" element={isAuthenticated && user?.isAdmin ? <SuperAdmin isAdminMode={true} /> : <Navigate to="/dashboard" />} />
            <Route path="/admin/affiliates" element={isAuthenticated && (user?.isSuperAdmin || user?.isAdmin) ? <AffiliatesAdmin /> : <Navigate to="/dashboard" />} />
            <Route path="/admin/partners" element={isAuthenticated && (user?.isSuperAdmin || user?.isAdmin) ? <PartnersAdmin /> : <Navigate to="/dashboard" />} />
            <Route path="/affiliates" element={<UkOnlyGate path="/affiliates"><AffiliateDashboard /></UkOnlyGate>} />

            <Route path="/settings" element={isAuthenticated ? <Settings /> : <Navigate to="/login" />} />
            <Route path="/support" element={isAuthenticated ? <Support /> : <Navigate to="/login" />} />
            <Route path="/gdpr" element={<UkOnlyGate path="/gdpr">{isAuthenticated ? <GDPRDashboard /> : <Navigate to="/login" />}</UkOnlyGate>} />
            <Route path="/features" element={<UkOnlyGate path="/features"><Features /></UkOnlyGate>} />
            <Route path="/security" element={<SecurityPage />} />
            {featureFlags.FINANCE_ENABLED && <Route path="/dashboard/finance" element={<UkOnlyGate path="/dashboard/finance">{isAuthenticated ? <FinanceDashboard /> : <Navigate to="/login" />}</UkOnlyGate>} />}
            <Route path="/nlp-analyzer" element={<UkOnlyGate path="/nlp-analyzer">{isAuthenticated ? <NLPAnalyzer /> : <Navigate to="/login" />}</UkOnlyGate>} />
            <Route path="/legal-qa" element={isAuthenticated ? <LegalQA /> : <Navigate to="/login" />} />

            {/* ========== CERTIFICATE ORDERING ROUTES (Phase 2) ========== */}
            <Route path="/certificate-orders/*" element={<UkOnlyGate path="/certificate-orders/*">{isAuthenticated ? <CertificateOrdering /> : <Navigate to="/login" />}</UkOnlyGate>} />

            {/* ========== FOOD SAFETY PRO ROUTES ========== */}
            <Route path="/food-safety-pro" element={<UkOnlyGate path="/food-safety-pro"><FoodSafetyLandingPage /></UkOnlyGate>} />
            <Route path="/food-safety" element={<UkOnlyGate path="/food-safety">{isAuthenticated ? <FoodSafetyDashboard /> : <Navigate to="/login" />}</UkOnlyGate>} />
            <Route path="/food-safety/dashboard" element={<UkOnlyGate path="/food-safety/dashboard">{isAuthenticated ? <FoodSafetyDashboard /> : <Navigate to="/login" />}</UkOnlyGate>} />
            {/* Future: /food-safety/establishments, /food-safety/daily-checks, etc. */}

            {/* Dynamic Root Route based on Domain */}
            <Route
              path="/"
              element={<RootRoute />}
            />
          </Routes>

          {/* Feedback Widget - Only show for authenticated users */}
          {isAuthenticated && <FeedbackWidget />}
          {/* F-201 AC-5: trial upgrade/expired prompts (renders only inside the window) */}
          <TrialConversionModal />
        </div>
      </PageViewTracker>
      {/* T-176-003: cookie-consent gate + opt-out control (renders until decided) */}
      <ConsentBanner />
      </JurisdictionProvider>
    </Router>
  );
}

export default App;